import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles } from "lucide-react";

export default function PublicSnippet() {
  const { slug } = useParams();
  const [snip, setSnip] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/snippets/${slug}`)
      .then(({ data }) => setSnip(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-sm text-[#9A938A]">This snippet has gone quiet.</p>
          <Link
            to="/"
            className="mt-4 inline-block text-xs uppercase tracking-wider text-[#D4A574]"
          >
            Meet a Colleague →
          </Link>
        </div>
      </div>
    );
  }

  if (!snip) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center">
        <p className="text-sm text-[#9A938A]">Loading…</p>
      </div>
    );
  }

  return (
    <div
      data-testid="public-snippet-page"
      className="min-h-screen bg-[#FBF9F6] flex flex-col"
    >
      <header className="max-w-5xl mx-auto w-full px-6 py-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#2D2926] hover:text-[#D4A574] transition-colors"
        >
          <Sparkles size={16} strokeWidth={1.5} className="text-[#D4A574]" />
          <span className="font-serif text-xl">Colleague</span>
        </Link>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-8 sm:py-16">
        <p className="text-xs uppercase tracking-[0.3em] text-[#9A938A] mb-6">
          Advice from a Colleague
        </p>
        <div className="relative">
          <span
            aria-hidden="true"
            className="absolute -top-12 -left-3 font-serif text-[120px] leading-none text-[#D4A574] select-none"
          >
            “
          </span>
          <blockquote
            data-testid="snippet-quote"
            className="relative font-serif text-2xl sm:text-3xl lg:text-4xl text-[#2D2926] leading-relaxed pl-2"
          >
            {snip.excerpt}
          </blockquote>
        </div>

        <div className="mt-10 flex items-center gap-4">
          {snip.persona_avatar_url && (
            <img
              src={snip.persona_avatar_url}
              alt={snip.persona_name}
              className="w-14 h-14 rounded-full object-cover ring-2 ring-[#D4A574]/30"
            />
          )}
          <div>
            <p className="font-serif text-xl text-[#2D2926]">
              — {snip.persona_name}
            </p>
            {snip.persona_role && (
              <p className="text-xs uppercase tracking-wider text-[#6B655D] mt-0.5">
                {snip.persona_role}
              </p>
            )}
          </div>
        </div>

        <div className="mt-14 sm:mt-20 bg-white border border-[#E6E2D8] rounded-3xl p-7 sm:p-8">
          <p className="font-serif text-2xl text-[#2D2926]">
            Want a colleague like {snip.persona_name?.split(" ")[0] || "this"}?
          </p>
          <p className="mt-2 text-sm text-[#6B655D] leading-relaxed">
            Colleague is your workplace friend down the hall — five colleagues
            who actually know HR, Marketing, Finance, Ops and Sales, plus Emma,
            Ryan, and Avi for the days you need a friend or a fresh angle.
            30 days free.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              data-testid="snippet-cta-signup"
              className="bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
            >
              Start free
            </Link>
            <Link
              to="/pricing"
              className="text-xs uppercase tracking-wider text-[#6B655D] hover:text-[#2D2926] transition-colors"
            >
              See pricing
            </Link>
          </div>
        </div>
      </main>

      <footer className="max-w-5xl mx-auto w-full px-6 py-8 text-[10px] uppercase tracking-wider text-[#9A938A]">
        Colleague · Built for the days at work
      </footer>
    </div>
  );
}
